import { useNavigate } from "@tanstack/react-router";

import { PageHeader } from "../shell/PageHeader";
import {
  useGetV1Plants,
  type EnvelopePlantListDataItemsItem,
  type Freshness,
  type FreshnessState,
} from "@solarkey/api-client";
import {
  CoverageBar,
  DataTable,
  EmptyState,
  ErrorState,
  Metric,
  Skeleton,
  type ColumnDef,
} from "@solarkey/ui";

type Row = EnvelopePlantListDataItemsItem;

const FRESHNESS_LABEL: Record<FreshnessState, string> = {
  live: "Live",
  stale: "Stale",
  path_down: "Path down",
};

/**
 * Scope-level Data — fleet-wide coverage + freshness overview (UX-005 §4.3).
 * Each row is a site with its signal coverage figure, freshness state and age.
 * Click a row to drill into the per-hop data path (P9). No colour except for
 * equipment health — freshness states use text labels only (UX-003 §3.2).
 */
export function ScopeData({
  tenant,
  scope,
}: {
  tenant: string;
  scope: string;
}): React.ReactNode {
  const navigate = useNavigate();
  const query = useGetV1Plants();

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-density-pad" aria-busy="true">
        <Skeleton variant="text" width="12rem" />
        <Skeleton variant="row" lines={6} />
      </div>
    );
  }

  if (query.isError || !query.data || query.data.status !== 200) {
    return (
      <ErrorState
        title="Data quality unavailable"
        message="Fleet coverage and freshness could not be loaded."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const rows = query.data.data.data.items ?? [];

  const columns: ColumnDef<Row, unknown>[] = [
    {
      accessorKey: "site_id",
      header: "Site",
      cell: (c) => c.row.original.site_id ?? "—",
    },
    {
      id: "coverage",
      header: "Coverage",
      cell: (c) => {
        const cov = c.row.original.coverage;
        if (!cov) return <span className="text-text-muted">—</span>;
        return (
          <span className="flex items-center gap-density-gap">
            <Metric figure={cov} />
            {typeof cov.coverage_pct === "number" ? (
              <CoverageBar pct={cov.coverage_pct} />
            ) : null}
          </span>
        );
      },
    },
    {
      id: "freshness",
      header: "Freshness",
      cell: (c) => freshnessCell(c.row.original.freshness),
    },
    {
      id: "freshness_age",
      header: "Age",
      cell: (c) => ageCell(c.row.original.freshness),
    },
    {
      id: "freshness_hop",
      header: "Bottleneck",
      cell: (c) => {
        const hop = c.row.original.freshness?.hop;
        return hop ? (
          <span>{humanize(hop)}</span>
        ) : (
          <span className="text-text-muted">—</span>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-density-pad">
      <PageHeader title="Data quality" scope="Coverage and freshness across the fleet." showTimeControl={false} />
      <DataTable<Row>
        columns={columns}
        rows={rows}
        getRowId={(r, i) => r.site_id ?? String(i)}
        onRowClick={(r) => {
          if (!r.site_id) return;
          void navigate({
            to: "/t/$tenant/s/$scope/plants/$site/datapath",
            params: { tenant, scope, site: r.site_id },
          });
        }}
        emptyState={
          <EmptyState variant="no_data" title="No sites with data in scope" />
        }
      />
    </div>
  );
}

function freshnessCell(f?: Freshness): React.ReactNode {
  if (!f?.state) return <span className="text-text-muted">—</span>;
  const label = FRESHNESS_LABEL[f.state] ?? f.state;
  return <span>{label}</span>;
}

function ageCell(f?: Freshness): React.ReactNode {
  if (typeof f?.age_s !== "number") {
    return <span className="text-text-muted">—</span>;
  }
  if (f.age_s < 60) return <span>{f.age_s}s</span>;
  if (f.age_s < 3600) return <span>{Math.round(f.age_s / 60)}m</span>;
  return <span>{Math.round(f.age_s / 3600)}h</span>;
}

function humanize(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
}
